import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import { Container } from "reactstrap";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";

/* -------------------------------------------------------------------------
 * AdminPartnerSettlementsPage — full partner settlements table view.
 * Similar pattern to CustomersPage, showing all partner settlements.
 * ---------------------------------------------------------------------- */

const columns = [
  {
    key: "name",
    label: "Partner Name",
    width: "1.6fr",
  },
  { key: "total_settlements", label: "Total Settlements", width: "1fr", align: "right" },
  { key: "total_amount", label: "Total Amount", width: "1fr", align: "right", render: (r) => `$${r.total_amount?.toLocaleString() || '0'}` },
  { key: "pending_amount", label: "Pending Amount", width: "1fr", align: "right", render: (r) => `$${r.pending_amount?.toLocaleString() || '0'}` },
  { key: "last_settlement", label: "Last Settlement", width: "1fr" },
];

const AdminPartnerSettlementsPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Partner Settlements | Omaha Food Lovers";
  const navigate = useNavigate();
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("total_amount");
  const [sortDirection, setSortDirection] = useState("desc");

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("page_size", 20);
    params.append("per_page", 20);
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const fetchSettlements = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.adminSettlementsCurrentPeriodBalances}?${buildQueryParams(pageNum)}`);

      if (response.data?.success) {
        const data = response.data.data;
        const balancesData = data.balances || [];
        const newSettlements = balancesData.map(item => ({
          id: item.vendor_listing_id || item.id,
          name: item.restaurant || item.name || 'Unknown',
          total_settlements: item.total_settlements || 1,
          total_amount: item.in_fee_owed || item.total_amount || 0,
          pending_amount: item.net_balance || item.pending_amount || 0,
          last_settlement: data.period?.end_date || item.last_settlement || 'N/A',
        }));

        if (isLoadMore) {
          setSettlements((prev) => [...prev, ...newSettlements]);
        } else {
          setSettlements(newSettlements);
        }

        // Simple pagination - if we got fewer results than page_size, no more data
        setHasMore(newSettlements.length === 20);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch partner settlements:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchSettlements(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleBackToDashboard = () => {
    navigate("/admin/settlements");
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "total_amount",
      options: [
        { value: "total_amount", label: "Total Amount" },
        { value: "pending_amount", label: "Pending Amount" },
        { value: "name", label: "Partner Name" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: setSortDirection,
      defaultValue: "desc",
    },
  ];

  useEffect(() => {
    fetchSettlements(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={handleBackToDashboard}
              style={{ backgroundColor: "transparent", color: "#1ba97f", border: "none", cursor: "pointer", fontSize: 14 }}
            >
              ← Back to Dashboard
            </button>
          </div>
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Partner Settlements</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of partner settlement transactions and payouts.</p>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by partner name"
            filters={filters}
          />

        <DataTable
          columns={columns}
          rows={settlements}
          getRowKey={(r) => r.id}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        />
        </div>
      </Container>
    </div>
  );
};

AdminPartnerSettlementsPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminPartnerSettlementsPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminPartnerSettlementsPage;
